import {
  CreativeDocumentModel,
  FrameRange,
} from 'content-creation-common/models';
import { media as mediaConstants } from 'content-creation-common/constants';
import { DEFAULT_HORIZONTAL_PADDING_FOR_TEXT } from 'content-creation-common/constants/text';

import {
  CONTENT_TYPE,
  CREATIVE_FORMAT_VERSION_V2,
  CREATIVE_FORMAT_VERSION_V3,
  CREATIVE_MODES,
  CURRENT_CREATIVE_FORMAT_VERSION,
  DEFAULT_TEXT_EFFECTS_OBJECTS,
  HIGHLIGHT_SIZE_OPTIONS,
  ERROR_CALL_TO_ACTION_MAP,
  ERROR_MAP,
  MEDIA_TYPE,
  TEST,
} from '../constants';
import { showErrorModal } from '../sagas/util/modelMethods';
import * as Log from './log';
import {
  deepCopy,
  isEmpty as isEmptyObject,
  isNil,
  isNotEmptyString,
  isNotNil,
  isObject,
  parseJSON,
} from './object';
import { isEmpty as isEmptyArray, sortBy } from './array';
import { isGroup } from '../features/group/util';

import {
  sanitizeContent,
  sanitizeMedia,
  sanitizeElements,
} from '../features/shared/creative/state-management/stateSanitizeUtils';
import {
  addSourceMediaCount,
  extractSkippedShots,
  restoreSkippedShots,
} from '../features/video-reformatting/state-management/documentModelUtils';
// eslint-disable-next-line max-len
import StateSanitizeUtilsFunctionalWorkerService from '../features/shared/creative/state-management/StateSanitizeUtilsWorkerService';
import { upgradeSourceAudioToShotAudio } from '../features/video-reformatting/state-management/utils';
import SequentialTaskQueue from '../model/tasks/SequentialTaskQueue';
import Task from '../model/tasks/Task';
import { getCreativeModeForRoute } from '../features/routes/state-management/selectors';
import { isNotNumber, isNumber } from './numbers';
import { captureDebugOnce } from './sentry';
import {
  DEFAULT_STROKE_COLOR,
  DEFAULT_STROKE_WIDTH,
  DEFAULT_TEXT_WRAP_BOX_HEIGHT_IN_PIXELS,
  DEFAULT_TEXT_WRAP_BOX_WIDTH_IN_PIXELS,
  DEPRECATED_TEXT_EFFECT_STROKE_COLOR,
  OVERFLOW_X,
  OVERFLOW_Y,
  TEXT_TYPE,
  TEXT_EFFECTS,
} from '../features/text/constants';
import { convertEmToPixel, getNumericFontValue } from './font';

const { MEDIA_PROCESSORS } = mediaConstants;

const mediaIdPrefixLength = 'media-'.length;
const clipIdPrefixLength = 'clip-'.length;
const textIdPrefixLength = 'text-'.length;

const sanitizeUtilsFunctionalWorkerService = new StateSanitizeUtilsFunctionalWorkerService();

function setAllMediaUploadComplete(creative) {
  const { media } = creative;
  if (isNotNil(media)) {
    const mediaIds = media.media;
    const mediaById = media.mediaById;
    if (!isEmptyArray(mediaIds)) {
      mediaIds.forEach((mediaId) => {
        const media = mediaById[mediaId];
        if (media) {
          media.uploadComplete = true;
        }
      });
    }
  }
}

export function setMediaProcessors(creativeData) {
  const { content, elements, media } = creativeData;

  if (isEmptyObject(elements)) return;
  if (isEmptyObject(content)) return;
  if (isEmptyObject(media)) return;

  const { elementsById } = elements;

  const { contentById } = content;

  const { mediaById } = media;

  const elementIds = Object.keys(elementsById);

  // update only video element
  elementIds.forEach((elementId) => {
    const element = elementsById[elementId];
    if (isEmptyObject(element)) return;

    const { contentId, meta } = element;

    const content = contentById[contentId];

    // check if video
    if (isEmptyObject(content) || content.contentType !== CONTENT_TYPE.VIDEO) {
      return;
    }

    const { mediaId } = content;
    const media = mediaById[mediaId];

    // if processors are already set, skip!
    if (!isEmptyArray(media.processors)) return;

    media.processors = [];

    // if element is not a shot, then assign MDP as its processor
    if (isEmptyObject(meta) || isNil(meta.shotType)) {
      media.processors.push(MEDIA_PROCESSORS.MDP);
    } else {
      media.processors.push(MEDIA_PROCESSORS.VIS);
    }
  });
}

export function upgradeCreativeFromV1ToV2(
  creativeData,
  creativeId,
  creativeName,
  outputMediaId,
) {
  Log.log('Upgrading creative format from V1 to V2...');

  const {
    content = {},
    clips = {},
    text = {},
    elements = {},
    media = {},
  } = creativeData;
  const { contentById = {} } = content;
  const { elements: elementsIndex = [], elementsById = {} } = elements;
  const { mediaById = {} } = media;

  const upgradedContentIndex = [];
  const upgradedContentById = {};
  const upgradedMediaIndex = [];
  const upgradedMediaById = {};

  const upgradedElementsById = elementsIndex.reduce((byId, elementId) => {
    const element = elementsById[elementId];
    const { contentId, contentType } = element;
    const upgradedElement = { ...element };

    let guid;
    let originalContent;
    let upgradedContent;
    let upgradedContentId;
    let mediaId;
    let originalMedia;
    let originalFileName;
    let upgradedMedia;

    switch (contentType) {
      case CONTENT_TYPE.IMAGE:
      case CONTENT_TYPE.SVG:
        // Upgrade the element
        guid = contentId.substr(mediaIdPrefixLength);
        upgradedContentId = `content-${guid}`;
        upgradedElement.contentId = upgradedContentId;

        // Get the original media
        mediaId = contentId;
        originalMedia = mediaById[mediaId];

        // Create new content
        upgradedContent = {
          contentId: upgradedContentId,
          contentType,
          mediaId,
        };

        // Upgrade the media
        upgradedMedia = { ...originalMedia };

        break;
      case 'CLIP': // old value of CONTENT_TYPE.VIDEO
        // Upgrade the element
        guid = contentId.substr(clipIdPrefixLength);
        upgradedContentId = `content-${guid}`;
        upgradedElement.contentId = upgradedContentId;

        // Get the original content and media
        originalContent = clips.clipsById[contentId];
        mediaId = originalContent.mediaId;
        originalMedia = mediaById[mediaId];

        // Upgrade the content
        upgradedContent = {
          ...originalContent,
          contentId: upgradedContentId,
          contentType: CONTENT_TYPE.VIDEO,
        };
        delete upgradedContent.clipId;

        // Upgrade the media
        upgradedMedia = { ...originalMedia };

        break;
      case CONTENT_TYPE.TEXT:
        // Upgrade the element
        guid = contentId.substr(textIdPrefixLength);
        upgradedContentId = `content-${guid}`;
        upgradedElement.contentId = upgradedContentId;

        // Get the original content and media
        originalContent = text.textById[contentId];
        mediaId = originalContent.mediaId;
        originalMedia = mediaById[mediaId];

        // Upgrade the content
        upgradedContent = {
          ...originalContent,
          contentId: upgradedContentId,
          contentType: CONTENT_TYPE.TEXT,
          font: originalMedia.font,
          fontSize: originalMedia.fontSize,
          fillColor: originalMedia.fillColor,
        };
        delete upgradedContent.textId;

        // Update the media
        upgradedMedia = {
          ...originalMedia,
          mediaType: MEDIA_TYPE.INLINE_SVG,
        };
        delete upgradedMedia.font;
        delete upgradedMedia.fontSize;
        delete upgradedMedia.fillColor;
        delete upgradedMedia.serverId;
        delete upgradedMedia.url;
        break;
      case 'CONTENT': // old value of CONTENT_TYPE.AUDIO
        // Upgrade the element
        upgradedContentId = contentId;

        // Get the original content and media
        originalContent = contentById[contentId];
        mediaId = originalContent.mediaId;
        originalMedia = mediaById[mediaId];

        // Upgrade the content
        upgradedContent = {
          ...originalContent,
          contentType: CONTENT_TYPE.AUDIO,
        };

        // Upgrade the media
        upgradedMedia = { ...originalMedia };
        break;
      case 'INLINE_SVG': // old value of CONTENT_TYPE.SHAPE
        // Upgrade the element
        guid = contentId.substr(mediaIdPrefixLength);
        upgradedContentId = `content-${guid}`;
        upgradedElement.contentId = upgradedContentId;

        // Get the original media
        mediaId = contentId;
        originalMedia = mediaById[mediaId];
        originalFileName = originalMedia.fileName;

        // Upgrade the content
        upgradedContent = {
          contentId: upgradedContentId,
          contentType: CONTENT_TYPE.SHAPE,
          mediaId,
          fillColor: originalMedia.fillColor,
          shape: originalFileName.replace(/\.svg/, '').toUpperCase(),
        };

        upgradedMedia = {
          ...originalMedia,
          mediaType: MEDIA_TYPE.INLINE_SVG,
        };
        delete upgradedMedia.fillColor;
        delete upgradedMedia.url;
        delete upgradedMedia.fileName;
        delete upgradedMedia.serverId;
        break;

      default:
        throw new Error('Unexpected content type', contentType);
    }

    // Finish upgrading content
    upgradedContentIndex.push(upgradedContentId);
    upgradedContentById[upgradedContentId] = upgradedContent;

    // Finish upgrading media
    if (element.svg) {
      upgradedMedia.svg = element.svg;
    }
    upgradedMediaIndex.push(mediaId);
    upgradedMediaById[mediaId] = upgradedMedia;

    // Finish upgrading element
    delete upgradedElement.contentType;
    delete upgradedElement.svg;
    byId[elementId] = upgradedElement;

    return byId;
  }, {});

  const upgradedCreative = {
    id: creativeId,
    name: creativeName,
    outputMediaId,
    version: CREATIVE_FORMAT_VERSION_V2,
  };

  const upgradedContent = {
    content: upgradedContentIndex,
    contentById: upgradedContentById,
  };
  const upgradedText = { isEditingTextId: null };
  const upgradedElements = { ...elements, elementsById: upgradedElementsById };
  const upgradedMedia = {
    media: upgradedMediaIndex,
    mediaById: upgradedMediaById,
  };

  return Object.assign(
    upgradedCreative,
    { elements: upgradedElements },
    { content: upgradedContent },
    { text: upgradedText },
    { media: upgradedMedia },
  );
}

function* checkCreativeFormatCompatibility(creativeData) {
  const creativeFormatVersion = creativeData.version || 1;

  // Abort when trying to load a creative with format version greater than the
  // currently defined version.
  if (creativeFormatVersion > CURRENT_CREATIVE_FORMAT_VERSION) {
    yield showErrorModal([
      ERROR_MAP.UNSUPPORTED_CREATIVE_FORMAT,
      ERROR_CALL_TO_ACTION_MAP.REFRESH,
    ]);
    throw new Error(
      `Unsupported creative format version ${creativeFormatVersion}`,
    );
  }
}

export const hasGroupWithCompositeRulesList = (creativeData) => {
  const { content = {} } = creativeData;
  const { contentById = {} } = content;

  const contentKeys = Object.keys(contentById);
  return contentKeys.some((contentId) => {
    const content = contentById[contentId];
    if (isNil(content)) return false;

    const { contentType, alphaCompositeRules } = content;
    return isGroup(contentType) && !isEmptyArray(alphaCompositeRules);
  });
};

export const hasContentWithMissingProperty = (creativeData, property) => {
  const { content = {} } = creativeData;
  const { contentById = {} } = content;

  const contentKeys = Object.keys(contentById);

  return contentKeys.some((contentId) => {
    const content = contentById[contentId];
    if (isNil(content)) return false;
    return !content.hasOwnProperty(property);
  });
};

export const hasElementsWithMissingProperty = (creativeData, property) => {
  const { elements = {} } = creativeData;
  const { elementsById = {} } = elements;

  const elementsKeys = Object.keys(elementsById);
  return elementsKeys.some((elementId) => {
    const element = elementsById[elementId];
    if (isNil(element)) return false;
    return !element.hasOwnProperty(property);
  });
};

export const hasInvalidCreativeMetadataProperty = (creativeData) => {
  const { metadata = {} } = creativeData;
  if (isEmptyObject(metadata)) return false;
  if (
    isNotNil(metadata.sourceVideoMediaId) ||
    isNotNil(metadata.sourceVideoServerId)
  ) {
    return true;
  }

  return false;
};

export const hasTextLayers = (creativeData) => {
  const { content = {} } = creativeData;
  const { contentById = {} } = content;

  const contentKeys = Object.keys(contentById);

  return contentKeys.some((contentId) => {
    const content = contentById[contentId];
    if (isNil(content)) return false;
    const { contentType } = content;
    return contentType === CONTENT_TYPE.TEXT;
  });
};

export const upgradeCompositeListsToElementCompositeRules = (creativeData) => {
  const { content = {}, elements = {} } = creativeData;
  const { contentById = {} } = content;
  const { elementsById = {} } = elements;

  const contentKeys = Object.keys(contentById);
  contentKeys.forEach((contentId) => {
    const content = contentById[contentId];
    if (isNil(content)) return;

    const { contentType } = content;
    if (isGroup(contentType)) {
      const { alphaCompositeRules, contentElementIds } = content;
      if (!isEmptyArray(alphaCompositeRules)) {
        alphaCompositeRules.forEach((rule, index) => {
          const elementId = contentElementIds[index];
          if (isNil(elementId)) return;

          const element = elementsById[elementId];
          if (isNil(element)) return;

          element.alphaCompositeRule = rule;
        });

        delete content.alphaCompositeRules;
      }
    }
  });

  return creativeData;
};

export const hasContentColors = (creativeData) => {
  const { content = {} } = creativeData;
  const { contentById = {} } = content;

  const contentIds = Object.keys(contentById);
  return contentIds.some((contentId) => {
    const content = contentById[contentId];
    return isNotNil(content) && isNotNil(content.colors);
  });
};

export const upgradeRootFrameEnd = (creativeData) => {
  // creative.elements.masks.root.frameEnd
  const { elements = {} } = creativeData;
  const { masks = {} } = elements;
  const { root = {} } = masks;

  root.frameEnd = root.frameEnd - 1;

  return creativeData;
};

export const upgradeColorsToMediaColors = (creativeData) => {
  const { content = {}, media = {} } = creativeData;
  const { contentById = {} } = content;
  const { mediaById = {} } = media;

  const contentIds = Object.keys(contentById);
  contentIds.forEach((contentId) => {
    const content = contentById[contentId];

    if (isNotNil(content) && !isEmptyObject(content.colors)) {
      const colors = deepCopy(content.colors);
      delete content.colors;

      const { mediaId } = content;
      const media = mediaById[mediaId];
      if (isNil(media) || isNil(colors.palettes)) return;

      const upgradedColors = [];
      const upgradedComplementaryColors = [];

      if (isNotNil(colors.bwColor)) {
        upgradedColors.push(colors.bwColor);
      }

      if (isNotNil(colors.averageColor)) {
        upgradedComplementaryColors.push(colors.averageColor);
      }

      colors.palettes.forEach(({ color, complementary }) => {
        if (isNotNil(color)) {
          upgradedColors.push(color);
        }

        if (isNotNil(complementary)) {
          upgradedComplementaryColors.push(complementary);
        }
      });

      media.colors = {
        colors: upgradedColors,
        complementary: upgradedComplementaryColors,
      };
    }
  });

  return creativeData;
};

export function upgradeContentVisibleProperty(creativeData) {
  return upgradeContentProperty(creativeData, 'visible', true, (content) => {
    return (
      content.contentType !== CONTENT_TYPE.AUDIO &&
      content.visible === undefined
    );
  });
}

export function upgradeElementSelectableProperty(creativeData) {
  return upgradeElementProperty(creativeData, 'selectable', true, (element) => {
    return element.selectable === undefined;
  });
}

/**
 * Upgrade content in CreativeJSON with property value, if predicate is passed in,
 * it must return true before the content is upgraded.
 * @param {CreativeJSON} creativeData
 * @param {String} property
 * @param {*} value
 * @param {(content) => Boolean} predicate
 */
export function upgradeContentProperty(
  creativeData,
  property,
  value,
  predicate,
) {
  const { content = {} } = creativeData;
  const { contentById = {} } = content;

  const contentKeys = Object.keys(contentById);
  contentKeys.forEach((contentId) => {
    const content = contentById[contentId];
    if (isNil(content)) return;
    if (isNotNil(predicate) && !predicate(content)) return;
    content[property] = value;
  });
  return creativeData;
}

/**
 * Upgrade elements in CreativeJSON with property value, if predicate is passed in,
 * it must return true before the element is upgraded.
 * @param {CreativeJSON} creativeData
 * @param {String} property
 * @param {*} value
 * @param {(content) => Boolean} predicate
 */
export function upgradeElementProperty(
  creativeData,
  property,
  value,
  predicate,
) {
  const { elements = {} } = creativeData;
  const { elementsById = {} } = elements;

  const elementsKeys = Object.keys(elementsById);
  elementsKeys.forEach((elementId) => {
    const element = elementsById[elementId];
    if (isNil(element)) return;
    if (isNotNil(predicate) && !predicate(element)) return;
    element[property] = value;
  });
  return creativeData;
}

/**
 * Upgrade metadata in CreativeJSON with
 * @param creativeJSON
 */
export function upgradeCreativeMetadataProperties(creativeJSON) {
  if (isEmptyObject(creativeJSON.metadata)) {
    creativeJSON.metadata = {};
  }

  const { metadata } = creativeJSON;
  if (metadata.hasOwnProperty('sourceVideoMediaId')) {
    metadata.sourceVideoServerIds = [metadata.sourceVideoMediaId];
    delete metadata.sourceVideoMediaId;
  }

  if (metadata.hasOwnProperty('sourceVideoServerId')) {
    metadata.sourceVideoServerIds = [metadata.sourceVideoServerId];
    delete metadata.sourceVideoServerId;
  }

  return creativeJSON;
}

/**
 * Deprecating text effects that are no longer valid for canvas text type
 *
 *
 * @param content
 * @returns {*}
 */
function upgradeCanvasText(content) {
  const { textEffect, textType } = content;
  if (textType !== TEXT_TYPE.CANVAS) return;
  if (isNil(textEffect)) return;

  const { type } = textEffect;

  switch (type) {
    case TEXT_EFFECTS.BEVEL:
      content.textEffect = {
        ...DEFAULT_TEXT_EFFECTS_OBJECTS[TEXT_EFFECTS.NONE],
      };
      break;
    case TEXT_EFFECTS.FIT_LINE_FILL:
    case TEXT_EFFECTS.LINE_FILL:
      content.textEffect = {
        ...DEFAULT_TEXT_EFFECTS_OBJECTS[TEXT_EFFECTS.HIGHLIGHT],
        ...content.textEffect,
        size:
          type === TEXT_EFFECTS.FIT_LINE_FILL
            ? HIGHLIGHT_SIZE_OPTIONS.TEXT
            : HIGHLIGHT_SIZE_OPTIONS.LINE,
        type: TEXT_EFFECTS.HIGHLIGHT,
      };
      break;
    case TEXT_EFFECTS.DROP_SHADOW_OUTLINE:
      textEffect.type = TEXT_EFFECTS.DROP_SHADOW;
      content.strokeColor = DEPRECATED_TEXT_EFFECT_STROKE_COLOR;
      content.strokeWidth = textEffect.size;
      break;
    case TEXT_EFFECTS.OUTLINE_STROKE:
      content.strokeColor = textEffect.color;
      content.strokeWidth = textEffect.size;
      content.textEffect = {
        ...DEFAULT_TEXT_EFFECTS_OBJECTS[TEXT_EFFECTS.NONE],
      };
      break;
    default:
      break;
  }
}

/**
 * Updates text layers that do not have wrapping enabled and forces them to have wrapping enabled
 * It uses the media width and height as the appropriate width/height of the wrapped text
 *
 * update text layers to include stroke color and width
 *
 * @param creativeJSON
 * @returns {*}
 */
export function upgradeCreativeText(creativeJSON) {
  const { content, media } = creativeJSON;

  const { contentById } = content;
  const { mediaById } = media;

  const contentKeys = Object.keys(contentById);
  contentKeys.forEach((contentKey) => {
    const content = contentById[contentKey];
    if (isNil(content)) return;

    const { contentType, mediaId, overflowX, textType } = content;

    if (contentType !== CONTENT_TYPE.TEXT) return;

    // If there is a media object, use its properties later to correct defaults
    const contentMedia = mediaById[mediaId];

    // If wrap isn't set, set it.
    if (overflowX !== OVERFLOW_X.WRAP && overflowX !== OVERFLOW_X.HIDDEN) {
      content.overflowX = OVERFLOW_X.WRAP;
      content.overflowY = OVERFLOW_Y.VISIBLE;
    }

    // Set default text wrap properties
    if (!isObject(content.textWrap)) {
      content.textWrap = {};
    }

    // Update breakWord
    if (isNil(content.textWrap.breakWord)) {
      content.textWrap.breakWord = false;
    }

    // Set the width/height if they are not set to the media width/height if it exists.
    if (isNotNil(contentMedia)) {
      const { height, width } = contentMedia;

      if (isNumber(height)) {
        if (isNotNumber(content.textWrap.height)) {
          content.textWrap.height = height;
        }

        if (isNotNumber(content.naturalHeight)) {
          content.naturalHeight = height;
        }
      }

      if (isNumber(width)) {
        if (isNotNumber(content.textWrap.width)) {
          content.textWrap.width = width;
        }

        if (isNotNumber(content.naturalWidth)) {
          content.naturalWidth = width;
        }
      }
    }

    // Set defaults for height and width if none exist
    if (isNotNumber(content.textWrap.height)) {
      content.textWrap.height = DEFAULT_TEXT_WRAP_BOX_HEIGHT_IN_PIXELS;
    }

    if (isNotNumber(content.textWrap.width)) {
      content.textWrap.width = DEFAULT_TEXT_WRAP_BOX_WIDTH_IN_PIXELS;
    }

    // Set stroke defaults
    if (isNil(content.strokeColor)) {
      content.strokeColor = DEFAULT_STROKE_COLOR;
    }

    if (isNotNumber(content.strokeWidth)) {
      content.strokeWidth = DEFAULT_STROKE_WIDTH;
    }

    // Upgrade layer to use CANVAS text type
    if (textType !== TEXT_TYPE.CANVAS) {
      content.textType = TEXT_TYPE.CANVAS;
      if (isNotNil(contentMedia) && isNotEmptyString(contentMedia.svg)) {
        delete contentMedia.svg;
      }

      // Update width to compensate for differences between SVG and CANVAS
      let wrapWidth = DEFAULT_TEXT_WRAP_BOX_WIDTH_IN_PIXELS;
      const { textWrap } = content;

      if (isNotNil(textWrap) && isNumber(textWrap.width)) {
        const leftRightPadding = convertEmToPixel(
          DEFAULT_HORIZONTAL_PADDING_FOR_TEXT,
          getNumericFontValue(content.fontSize),
        );
        wrapWidth = textWrap.width;
        content.textWrap.width = wrapWidth + 2 * leftRightPadding;
      }

      // NOTE: We further update the natural width/height of each text layer after loading fonts in
      // the upgradeTextLayers method as part of loading a creative.
      content.naturalWidth = null;
      content.naturalHeight = null;
    }

    upgradeCanvasText(content);
  });

  return creativeJSON;
}

// previously we had a bug where we added element(s) of a group in root elements
// this method is to sanitize existing creatives with this bug
export const cleanUpRootElements = (creativeData, creativeId) => {
  const { content, elements } = creativeData;

  if (isEmptyObject(elements)) return;
  if (isEmptyObject(content)) return;

  const { elements: rootElementIds, elementsById } = elements;
  const { contentById } = content;

  const elementIds = Object.keys(elementsById);

  // children layers were not setting group Id correctly
  // update only video element
  elementIds.forEach((elementId) => {
    const element = elementsById[elementId];
    if (isEmptyObject(element)) return;

    const { contentId } = element;
    const content = contentById[contentId];

    const { contentElementIds } = content;
    if (isEmptyArray(contentElementIds)) return;

    contentElementIds.forEach((contentElementId) => {
      // set groupId for child element
      elementsById[contentElementId].groupId = elementId;
      elementsById[contentElementId].parentContentId = contentId;

      // remove if exits in root element
      const index = rootElementIds.indexOf(contentElementId);
      if (index !== -1) {
        rootElementIds.splice(index, 1);

        captureDebugOnce("Cleaned up creative's root elements", { creativeId });
      }
    });
  });
};

export function* upgradeCreativeIfNecessary(
  creativeData,
  creativeId,
  creativeMode,
  creativeName,
  outputMediaId,
) {
  yield checkCreativeFormatCompatibility(creativeData);

  creativeData = deepCopy(creativeData);

  // This operation should go first as it restores the creative's data
  // It will merge elements, content, media from creative.metadata.skippedShotData.
  if (creativeMode === CREATIVE_MODES.SHOWS) {
    creativeData = restoreSkippedShots(creativeData);
  }

  // Upgrade from previous version to new creative version data model
  if (
    !creativeData.version ||
    creativeData.version < CREATIVE_FORMAT_VERSION_V2
  ) {
    creativeData = upgradeCreativeFromV1ToV2(
      creativeData,
      creativeId,
      creativeName,
      outputMediaId,
    );
  }

  // Upgrade creatives that had been previously saved with an off-by-one frameEnd on the root mask.
  if (creativeData.version < CREATIVE_FORMAT_VERSION_V3) {
    creativeData = upgradeRootFrameEnd(creativeData);
  }

  // Check for old data patterns and switch them
  if (creativeData.version >= CREATIVE_FORMAT_VERSION_V2) {
    // Upgrade composite rules (for older creatives with groups made in the old style)
    if (hasGroupWithCompositeRulesList(creativeData)) {
      creativeData = upgradeCompositeListsToElementCompositeRules(creativeData);
    }

    // Move media color information from content to media obj
    if (hasContentColors(creativeData)) {
      creativeData = upgradeColorsToMediaColors(creativeData);
    }

    // Set all elements to selectable by default
    if (hasElementsWithMissingProperty(creativeData, 'selectable')) {
      creativeData = upgradeElementSelectableProperty(creativeData);
    }

    // Update creative metadata properties
    if (hasInvalidCreativeMetadataProperty(creativeData)) {
      creativeData = upgradeCreativeMetadataProperties(creativeData);
    }

    // If the creative has old-style text layers that do not wrap, convert them to wrapping layers with
    // a default width and height set
    if (hasTextLayers(creativeData)) {
      creativeData = upgradeCreativeText(creativeData);
    }

    if (creativeMode === CREATIVE_MODES.SHOWS) {
      creativeData = upgradeSourceAudioToShotAudio(creativeData);
    }
  }

  cleanUpRootElements(creativeData, creativeId);

  setAllMediaUploadComplete(creativeData);
  setMediaProcessors(creativeData);

  /*
   NOTE: The newest creative version number is always added when the creative is saved.
   */

  return creativeData;
}

/**
 * Some of these sanitize methods expect there will not be repeated content nor media ids.
 * We currently do not repeat them, even creating new ones on duplicate.
 * @param {ReduxState} state
 * @returns CreativeDocumentModel
 */
export const getCreativeDocumentModelFromState = (state) => {
  const { content, creative, elements, media, playback, text, ui } = state;

  const relevantState = deepCopy({
    content,
    creative,
    elements,
    media,
    playback,
    text,
    ui,
  });

  let { creativeMode } = playback;

  if (isNil(creativeMode)) {
    creativeMode = getCreativeModeForRoute(state);
  }

  const taskQueue = new SequentialTaskQueue();
  const initializeModelTask = new Task(() => {
    const { metadata } = creative;
    return CreativeDocumentModel.fromJSON({
      content,
      creativeMode,
      elements,
      media,
      metadata,
    });
  });

  const getSanitizeUtilsWorkerTask = (callback, fallback) => {
    return new Task((creativeDocumentModel) => {
      return new Promise((resolve) => {
        callback(creativeDocumentModel, relevantState)
          .then((documentModelJSON) => {
            resolve(CreativeDocumentModel.fromJSON(documentModelJSON));
          })
          .catch((err) => {
            if (!TEST) {
              const errorMsg =
                'We failed to process creative asynchronously for some reason, trying to process syncronously.';
              Log.warn(errorMsg, err);
            }
            resolve(fallback(creativeDocumentModel, relevantState));
          });
      });
    });
  };

  const sanitizeElementsTask = getSanitizeUtilsWorkerTask(
    sanitizeUtilsFunctionalWorkerService.sanitizeElements,
    sanitizeElements,
  );

  const sanitizeContentTask = new Task((creativeDocumentModel) => {
    return sanitizeContent(creativeDocumentModel, relevantState);
  });

  const sanitizeMediaTask = new Task((creativeDocumentModel) => {
    return sanitizeMedia(creativeDocumentModel);
  });

  // Add tasks
  taskQueue.addTask(initializeModelTask);
  taskQueue.addTask(sanitizeElementsTask);
  taskQueue.addTask(sanitizeContentTask);
  taskQueue.addTask(sanitizeMediaTask);

  // Move shots AFTER we sanitize so we don't miss anything like localUrls on media
  if (creativeMode === CREATIVE_MODES.SHOWS) {
    const extractSkippedShotsTask = getSanitizeUtilsWorkerTask(
      sanitizeUtilsFunctionalWorkerService.extractSkippedShots,
      extractSkippedShots,
    );
    taskQueue.addTask(extractSkippedShotsTask);

    const addSourceMediaCountTask = new Task((creativeDocumentModel) => {
      return addSourceMediaCount(creativeDocumentModel);
    });

    taskQueue.addTask(addSourceMediaCountTask);
  }

  return taskQueue.run();
};

export const getCreativeDetailsFrom = (state) => {
  const {
    playback: {
      creativeId,
      creativeName,
      creativeTemplateId,
      creativeUpdatedAt,
      creativeVersionId,
    },
  } = state;

  return {
    creativeId,
    creativeName,
    creativeTemplateId,
    creativeUpdatedAt,
    creativeVersionId,
  };
};

export const getMetadataForCreativeVersion = (creativeVersion) => {
  const {
    ads_api_media_id: adsAPIMediaId,
    data: creativeDataJSON,
    errors: creativeVersionErrors,
    output_media_entities: outputMediaEntities,
    rsp_asset_id: rspAssetId,
  } = creativeVersion;

  return {
    adsAPIMediaId,
    creativeDataJSON,
    creativeVersionErrors,
    outputMediaEntities,
    rspAssetId,
  };
};

export const getMetadataForCreative = (creative) => {
  const {
    id,
    name,
    ad_account_id: adAccountId,
    created_by: createdBy,
    creative_mode: creativeMode,
    edition_id: editionId,
    editor_mode: editorMode,
    organization_id: organizationId,
    publisher_id: publisherId,
    updated_at: updatedAt,
  } = creative;

  const versionMetaData = getMetadataForCreativeVersion(creative.version);
  const {
    adsAPIMediaId,
    creativeDataJSON,
    creativeVersionErrors,
    outputMediaEntities,
    outputMediaId,
    rspAssetId,
  } = versionMetaData;

  // TODO make model for this.
  const metaData = {
    adAccountId,
    adsAPIMediaId,
    createdBy,
    creativeMode,
    creativeVersionErrors,
    editionId,
    editorMode,
    id,
    name,
    organizationId,
    outputMediaEntities,
    outputMediaId,
    publisherId,
    rspAssetId,
    updatedAt,
  };

  if (isNil(creativeDataJSON)) return metaData;

  try {
    const creativeData = parseJSON(creativeDataJSON);
    return Object.assign(creativeData, metaData);
  } catch (error) {
    return metaData;
  }
};

export const getCreativeVersionZippedUrl = (
  creativeId,
  versionId,
  filename = 'combined.zip',
) => {
  if (isNil(creativeId) || isNil(versionId)) {
    throw new Error(
      'Refusing to generate URL without creativeId and versionId',
    );
  }
  return `/api/v1/creatives/${creativeId}/versions/${versionId}/zip/${encodeURIComponent(
    filename,
  )}`;
};

export function getImplicitBreaksForChapters(chapters, maxFramesPerChapter) {
  if (isNotNumber(maxFramesPerChapter)) {
    throw new Error('Must provide a valid maxFramesPerChapter');
  }

  return chapters.reduce((chapterBreaks, chapter) => {
    const chapterLength = chapter.end - chapter.start + 1;
    if (chapterLength <= maxFramesPerChapter) {
      // chapter length ok, keep as is
      return chapterBreaks.concat(chapter);
    }
    // chapter too long, divide it evenly
    const targetCount = Math.ceil(chapterLength / maxFramesPerChapter);
    const targetLength = Math.ceil(chapterLength / targetCount);
    let start = chapter.start;
    let end = chapter.end;
    for (let i = 0; i < targetCount; i++) {
      if (i > 0) start = end + 1;
      end = i + 1 === targetCount ? chapter.end : start + targetLength;
      chapterBreaks.push(new FrameRange(start, end));
    }
    return chapterBreaks;
  }, []);
}

export function getShotsForChapter(allShots, chapterFrameRange) {
  if (isNil(allShots)) return undefined;

  const sortedAllShots = sortBy(allShots, 'start');

  return sortedAllShots.reduce((selectedShots, shotGroup) => {
    const shotRange = new FrameRange(shotGroup.start, shotGroup.end);

    // It is possible, although unlikely, that a chapter could be between the start and end of a shot.
    // This makes sure that in this case we include the partial shot.
    // This should happen infrequently.
    if (chapterFrameRange.intersects(shotRange)) {
      if (!chapterFrameRange.contains(shotRange)) {
        Log.warn(
          'Chapter overlaps shot boundaries',
          chapterFrameRange,
          shotRange,
        );
        captureDebugOnce('Chapter overlaps shot boundaries', {
          chapterFrameRange,
          shotRange,
        });
      }

      const intersection = FrameRange.intersection(
        chapterFrameRange,
        shotRange,
      );
      const { end, start } = intersection;

      // TODO let this use shots (would have to update tests)
      selectedShots.push({
        end,
        start,
      });
    }
    return selectedShots;
  }, []);
}
